import { Component, ElementRef, inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { CommonModalService, GungLanguageConfigService, GungNotificationService, Language, OptionsListMulti } from 'gung-common';
import { catchError, finalize, forkJoin, interval, map, Observable, of, startWith, Subject, switchMap, takeUntil, throwError } from 'rxjs';
import {
  CatalogueGeneratorHistoryMessage,
  CatalogueGeneratorHistoryResponse,
  CatalogueGeneratorResponse,
  PDF_DOCUMENT_BUILDER_ATTACHMENT_ACCEPT,
  PDF_DOCUMENT_BUILDER_MAX_ATTACHMENT_BYTES,
  PDF_DOCUMENT_BUILDER_MAX_ATTACHMENTS,
  PDF_DOCUMENT_BUILDER_SUPPORTED_MIME_TYPES,
  PdfDocumentBuilderAttachmentDraft,
  PdfDocumentBuilderAttachmentRequest,
  PdfDocumentTemplate,
  PdfDocumentTemplateEditableFields,
  PdfDocumentTemplateGroupSettings,
  PdfDocumentTemplateStatus,
  PdfDocumentType
} from '../../models/pdf-document-builder.model';
import { PdfDocumentBuilderService } from '../../services/pdf-document-builder/pdf-document-builder.service';
import { GungFlowService } from '../../../../services/gung-flow/gung-flow.service';
import { UsersService } from '../../../../services/users/users.service';
import { GungFlow } from '../../../../state/flow/types';

type PdfDocumentBuilderMode = 'idle' | 'new' | 'edit';
type PdfDocumentBuilderChatRole = 'user' | 'assistant';
type PdfDocumentBuilderChatKind = 'text' | 'question' | 'result' | 'error';

const HIDDEN_GENERATOR_CONTEXT_START = '\n\n--- HIDDEN PDF DOCUMENT BUILDER CONTEXT ---\n';
const HIDDEN_GENERATOR_CONTEXT_END = '\n--- END HIDDEN PDF DOCUMENT BUILDER CONTEXT ---';

const GENERATION_STATUS_WORDS = [
  'Bloviating', 'Ruminating', 'Cogitating', 'Pondering', 'Mulling',
  'Concocting', 'Brewing', 'Crafting', 'Stitching', 'Weaving',
  'Tinkering', 'Calibrating', 'Synthesizing', 'Distilling', 'Marinating',
  'Assembling', 'Arranging', 'Composing', 'Sketching', 'Drafting',
  'Noodling', 'Percolating', 'Simmering', 'Whisking'
];
const GENERATION_WORD_ROTATION_MS = 5000;

interface PdfDocumentBuilderChatMessage {
  id: number;
  role: PdfDocumentBuilderChatRole;
  kind: PdfDocumentBuilderChatKind;
  text: string;
  detail?: string;
}

@Component({
  selector: 'lib-pdf-document-builder',
  standalone: false,
  templateUrl: './pdf-document-builder.component.html',
  styleUrl: './pdf-document-builder.component.scss'
})
export class PdfDocumentBuilderComponent implements OnInit, OnDestroy {
  protected readonly pdfDocumentBuilderService = inject(PdfDocumentBuilderService);
  protected readonly modalService = inject(CommonModalService);
  protected readonly notificationService = inject(GungNotificationService);
  protected readonly sanitizer = inject(DomSanitizer);
  protected readonly languageConfigService = inject(GungLanguageConfigService);
  protected readonly usersService = inject(UsersService);
  protected readonly gungFlowService = inject(GungFlowService);

  readonly statusOptions: PdfDocumentTemplateStatus[] = ['DRAFT', 'PUBLISHED', 'ARCHIVED'];

  documentTypes: PdfDocumentType[] = [];
  templates: PdfDocumentTemplate[] = [];
  versionHistory: PdfDocumentTemplate[] = [];

  selectedTemplate: PdfDocumentTemplate;
  templateForm: PdfDocumentTemplateEditableFields = this.createEmptyTemplateForm();

  documentTypeFilter = '';
  statusFilter: PdfDocumentTemplateStatus | '' = '';
  searchTerm = '';

  groupSettingsExpanded = false;
  groupSettingsForm: PdfDocumentTemplateGroupSettings = this.createEmptyGroupSettingsForm();
  availableLocales: Language[] = [];
  availableRoles: string[] = [];
  availableUserGroups: string[] = [];
  availableFlows: GungFlow[] = [];
  roleOptions: OptionsListMulti[] = [];
  userGroupOptions: OptionsListMulti[] = [];
  flowOptions: OptionsListMulti[] = [];
  savingGroupSettings = false;

  mode: PdfDocumentBuilderMode = 'idle';
  loadingInitialData = false;
  loadingLibrary = false;
  loadingTemplate = false;
  saving = false;
  deleting = false;
  sendingMessage = false;
  creatingDraftForChat = false;
  loadingChatHistory = false;

  chatDraft = '';
  chatMessages: PdfDocumentBuilderChatMessage[] = [];
  generatedHtmlPendingSave = false;
  generatedPdfUrl = '';
  generatedPdfSafeUrl: SafeResourceUrl | null = null;
  pdfPreviewError = '';
  sourceExpanded = false;
  generationStatusText = '';

  readonly attachmentAccept = PDF_DOCUMENT_BUILDER_ATTACHMENT_ACCEPT;
  readonly maxAttachments = PDF_DOCUMENT_BUILDER_MAX_ATTACHMENTS;
  pendingAttachments: PdfDocumentBuilderAttachmentDraft[] = [];

  @ViewChild('attachmentInput') private attachmentInput?: ElementRef<HTMLInputElement>;

  private chatMessageId = 0;
  private attachmentLocalIdSeq = 0;
  private readonly destroy$ = new Subject<void>();
  private readonly generationStatusStop$ = new Subject<void>();
  private generationStartedAt: number | null = null;

  ngOnInit(): void {
    this.loadInitialData();
  }

  ngOnDestroy(): void {
    this.clearGenerationStatusTimer();
    this.generationStatusStop$.complete();
    this.destroy$.next();
    this.destroy$.complete();
  }

  get visibleTemplates(): PdfDocumentTemplate[] {
    const query = this.searchTerm.trim().toLowerCase();

    if (!query) {
      return this.templates;
    }

    return this.templates.filter(template => {
      return [
        template.name,
        template.description,
        template.documentTypeId,
        template.templateGroupId,
        this.getDocumentTypeName(template.documentTypeId)
      ]
        .filter(Boolean)
        .some(value => value.toLowerCase().includes(query));
    });
  }

  get selectedDocumentType(): PdfDocumentType {
    return this.documentTypes.find(type => type.key === this.templateForm.documentTypeId);
  }

  get canEdit(): boolean {
    return this.mode === 'new' || (this.mode === 'edit' && this.selectedTemplate?.status === 'DRAFT');
  }

  get canSave(): boolean {
    return this.canEdit && !!this.templateForm.name?.trim() && !!this.templateForm.documentTypeId && !this.saving;
  }

  get canPublish(): boolean {
    return this.mode === 'edit' && this.selectedTemplate?.status === 'DRAFT' && !this.saving;
  }

  get canArchive(): boolean {
    return this.mode === 'edit' && !!this.selectedTemplate && this.selectedTemplate.status !== 'ARCHIVED' && !this.saving;
  }

  get canCreateNewVersion(): boolean {
    return this.mode === 'edit' && !!this.selectedTemplate && this.selectedTemplate.status !== 'DRAFT' && !this.saving;
  }

  get hasGeneratedPreview(): boolean {
    return !!this.generatedPdfSafeUrl;
  }

  get canSendChatMessage(): boolean {
    const hasReadyAttachment = this.pendingAttachments.some(a => a.status === 'ready');
    const hasContent = !!this.chatDraft.trim() || hasReadyAttachment;
    const hasUploadingAttachment = this.pendingAttachments.some(a => a.status === 'uploading');
    const hasErroredAttachment = this.pendingAttachments.some(a => a.status === 'error');

    return (
      this.canEdit &&
      !!this.templateForm.documentTypeId &&
      !!this.templateForm.name?.trim() &&
      hasContent &&
      !hasUploadingAttachment &&
      !hasErroredAttachment &&
      !this.sendingMessage &&
      !this.creatingDraftForChat &&
      !this.saving &&
      !this.loadingTemplate &&
      !this.loadingChatHistory
    );
  }

  get canPickAttachment(): boolean {
    return (
      this.canEdit &&
      !this.sendingMessage &&
      !this.creatingDraftForChat &&
      this.pendingAttachments.length < this.maxAttachments
    );
  }

  get chatUnavailableMessage(): string {
    if (this.loadingChatHistory) {
      return 'Loading conversation history.';
    }

    if (!this.canEdit && this.mode === 'edit') {
      return 'Create a draft version to refine this template.';
    }

    if (!this.templateForm.documentTypeId || !this.templateForm.name?.trim()) {
      return 'Select a document type and name before generating.';
    }

    return '';
  }

  loadInitialData(): void {
    this.loadingInitialData = true;
    this.availableLocales = this.languageConfigService.getLanguages().filter(lang => lang.enabled);
    forkJoin({
      documentTypes: this.pdfDocumentBuilderService.getDocumentTypes(),
      templates: this.pdfDocumentBuilderService.getTemplates({ latestOnly: true }),
      roles: this.usersService.getDefaultUserRoles().pipe(catchError(() => of([] as { id: string }[]))),
      systemSettings: this.gungFlowService.getSystemGung().pipe(catchError(() => of(null))),
      adminFlows: this.gungFlowService.getGungAdminFlows().pipe(catchError(() => of([] as GungFlow[])))
    })
      .pipe(finalize(() => (this.loadingInitialData = false)))
      .subscribe({
        next: ({ documentTypes, templates, roles, systemSettings, adminFlows }) => {
          this.documentTypes = documentTypes || [];
          this.templates = templates || [];
          this.availableRoles = (roles || []).map(role => role.id).filter(Boolean);
          this.availableUserGroups = systemSettings?.extra?.userGroups || [];
          this.availableFlows = adminFlows || [];

          this.clearWorkspaceSelection();
        },
        error: () => this.notifyError('Could not load document builder data')
      });
  }

  loadTemplates(): void {
    this.loadingLibrary = true;
    this.pdfDocumentBuilderService
      .getTemplates({
        documentTypeId: this.documentTypeFilter || undefined,
        status: this.statusFilter || undefined,
        latestOnly: true
      })
      .pipe(finalize(() => (this.loadingLibrary = false)))
      .subscribe({
        next: templates => (this.templates = templates || []),
        error: () => this.notifyError('Could not load templates')
      });
  }

  onFilterChange(): void {
    this.loadTemplates();
  }

  startNewTemplate(): void {
    this.resetGeneratorWorkspace();
    this.mode = 'new';
    this.selectedTemplate = null;
    this.versionHistory = [];
    this.templateForm = this.createEmptyTemplateForm();
    this.templateForm.documentTypeId = this.documentTypeFilter || this.documentTypes[0]?.key || '';
  }

  openTemplate(template: PdfDocumentTemplate): void {
    if (!template?.id) {
      return;
    }

    this.openTemplateById(template.id);
  }

  openTemplateById(id: string): void {
    this.resetGeneratorWorkspace();
    this.loadingTemplate = true;
    this.pdfDocumentBuilderService
      .getTemplate(id)
      .pipe(
        switchMap(template =>
          forkJoin({
            template: of(template),
            versions: this.pdfDocumentBuilderService.getTemplateVersions(template.templateGroupId)
          })
        ),
        finalize(() => (this.loadingTemplate = false))
      )
      .subscribe({
        next: ({ template, versions }) => {
          this.applyTemplate(template, versions);
          this.loadConversationHistory(template);
        },
        error: () => this.notifyError('Could not open template')
      });
  }

  saveTemplate(): void {
    if (!this.canSave) {
      return;
    }

    this.saving = true;
    const request = this.mode === 'new'
      ? this.pdfDocumentBuilderService.createTemplate(this.buildRequest())
      : this.pdfDocumentBuilderService.updateTemplate(this.selectedTemplate.id, this.buildRequest());

    request
      .pipe(finalize(() => (this.saving = false)))
      .subscribe({
        next: template => this.openReturnedTemplate(template, 'Template saved'),
        error: error => {
          if (error?.status === 409) {
            this.notifyError('Only draft versions can be updated');
            this.openTemplateById(this.selectedTemplate.id);
            return;
          }

          this.notifyError('Could not save template');
        }
      });
  }

  sendChatMessage(): void {
    if (!this.canSendChatMessage) {
      return;
    }

    const message = this.chatDraft.trim();
    const readyAttachments = this.pendingAttachments.filter(a => a.status === 'ready');
    const requestAttachments: PdfDocumentBuilderAttachmentRequest[] = readyAttachments.map(a => ({
      url: a.url!,
      mimeType: a.mimeType,
      filename: a.filename
    }));
    const attachmentSummary = readyAttachments.length > 0
      ? `Attached: ${readyAttachments.map(a => a.filename).join(', ')}`
      : undefined;

    this.chatDraft = '';
    this.pendingAttachments = [];
    const displayMessage = message || attachmentSummary || '';
    const generatorMessage = this.buildGeneratorMessage(message, this.shouldSendInitialGeneratorContext());
    this.appendChatMessage('user', 'text', displayMessage, message ? attachmentSummary : undefined);
    this.sendingMessage = true;
    this.generationStartedAt = Date.now();
    this.startGenerationStatusTimer();

    this.ensureDraftForChat()
      .pipe(
        switchMap(template => {
          const submittedTemplateId = template.id;
          return this.pdfDocumentBuilderService
            .generateCatalogue(this.getConversationId(template), generatorMessage, requestAttachments)
            .pipe(map(response => ({ response, submittedTemplateId })));
        }),
        takeUntil(this.destroy$),
        finalize(() => {
          this.sendingMessage = false;
          this.generationStatusText = '';
          this.clearGenerationStatusTimer();
        })
      )
      .subscribe({
        next: ({ response, submittedTemplateId }) => {
          if (this.selectedTemplate?.id !== submittedTemplateId) {
            return;
          }

          this.handleGeneratorResponse(response);
        },
        error: error => {
          const message = this.getGeneratorErrorMessage(error);
          this.appendChatMessage('assistant', 'error', message);
          this.notifyError(message);
        }
      });
  }

  triggerAttachmentPicker(): void {
    if (!this.canPickAttachment) {
      return;
    }
    this.attachmentInput?.nativeElement.click();
  }

  onAttachmentsSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const files = Array.from(input.files || []);
    input.value = '';

    if (files.length === 0) {
      return;
    }

    for (const file of files) {
      const mimeType = this.resolveAttachmentMimeType(file);
      const draft: PdfDocumentBuilderAttachmentDraft = {
        localId: ++this.attachmentLocalIdSeq,
        file,
        mimeType,
        filename: file.name,
        status: 'uploading'
      };

      const validationError = this.validateAttachment(draft);
      if (validationError) {
        this.notifyError(validationError);
        continue;
      }

      this.pendingAttachments = [...this.pendingAttachments, draft];
      this.uploadAttachmentDraft(draft);
    }
  }

  removeAttachment(localId: number): void {
    this.pendingAttachments = this.pendingAttachments.filter(a => a.localId !== localId);
  }

  trackByAttachmentId(_index: number, attachment: PdfDocumentBuilderAttachmentDraft): number {
    return attachment.localId;
  }

  attachmentIcon(attachment: PdfDocumentBuilderAttachmentDraft): string {
    if (attachment.status === 'uploading') {
      return 'fa-spinner fa-spin';
    }
    if (attachment.status === 'error') {
      return 'fa-triangle-exclamation';
    }
    if (attachment.mimeType.startsWith('image/')) {
      return 'fa-image';
    }
    if (attachment.mimeType === 'application/pdf') {
      return 'fa-file-pdf';
    }
    return 'fa-file-lines';
  }

  private uploadAttachmentDraft(draft: PdfDocumentBuilderAttachmentDraft): void {
    const conversationId = this.selectedTemplate
      ? this.getConversationId(this.selectedTemplate)
      : 'pending';

    this.pdfDocumentBuilderService
      .uploadAttachment(draft.file, conversationId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: ({ s3Key, url }) => {
          this.pendingAttachments = this.pendingAttachments.map(a =>
            a.localId === draft.localId ? { ...a, status: 'ready', s3Key, url } : a
          );
        },
        error: error => {
          const message = error?.message || 'Could not upload attachment';
          this.pendingAttachments = this.pendingAttachments.map(a =>
            a.localId === draft.localId ? { ...a, status: 'error', error: message } : a
          );
          this.notifyError(`Could not upload ${draft.filename}`);
        }
      });
  }

  private resolveAttachmentMimeType(file: File): string {
    if (file.type) {
      return file.type;
    }
    const name = file.name.toLowerCase();
    if (name.endsWith('.md') || name.endsWith('.markdown')) {
      return 'text/markdown';
    }
    if (name.endsWith('.txt')) {
      return 'text/plain';
    }
    if (name.endsWith('.pdf')) {
      return 'application/pdf';
    }
    return 'application/octet-stream';
  }

  private validateAttachment(draft: PdfDocumentBuilderAttachmentDraft): string | null {
    if (this.pendingAttachments.length >= this.maxAttachments) {
      return `Maximum ${this.maxAttachments} attachments allowed`;
    }
    if (!PDF_DOCUMENT_BUILDER_SUPPORTED_MIME_TYPES.includes(draft.mimeType)) {
      return `Unsupported file type: ${draft.filename}`;
    }
    if (draft.file.size > PDF_DOCUMENT_BUILDER_MAX_ATTACHMENT_BYTES) {
      const mb = Math.round(PDF_DOCUMENT_BUILDER_MAX_ATTACHMENT_BYTES / (1024 * 1024));
      return `${draft.filename} exceeds the ${mb} MB limit`;
    }
    return null;
  }

  private startGenerationStatusTimer(): void {
    this.generationStatusStop$.next();

    interval(1000)
      .pipe(
        startWith(0),
        takeUntil(this.generationStatusStop$),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        if (this.generationStartedAt === null) {
          return;
        }
        const elapsed = Date.now() - this.generationStartedAt;
        this.generationStatusText = `${this.pickStatusWord(elapsed)}… (${this.formatElapsed(elapsed)})`;
      });
  }

  private clearGenerationStatusTimer(): void {
    this.generationStatusStop$.next();
    this.generationStartedAt = null;
    this.generationStatusText = '';
  }

  private pickStatusWord(elapsedMs: number): string {
    const idx = Math.floor(Math.max(0, elapsedMs) / GENERATION_WORD_ROTATION_MS) % GENERATION_STATUS_WORDS.length;
    return GENERATION_STATUS_WORDS[idx];
  }

  private formatElapsed(ms: number): string {
    const totalSec = Math.max(0, Math.floor(ms / 1000));
    if (totalSec < 60) {
      return `${totalSec}s`;
    }
    const m = Math.floor(totalSec / 60);
    const s = totalSec % 60;
    if (m < 60) {
      return `${m}m ${s}s`;
    }
    const h = Math.floor(m / 60);
    return `${h}h ${m % 60}m`;
  }

  onChatComposerKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.sendChatMessage();
    }
  }

  publishTemplate(): void {
    if (!this.canPublish) {
      return;
    }

    this.saving = true;
    this.pdfDocumentBuilderService
      .publishTemplate(this.selectedTemplate.id)
      .pipe(finalize(() => (this.saving = false)))
      .subscribe({
        next: template => this.openReturnedTemplate(template || { ...this.selectedTemplate, status: 'PUBLISHED' }, 'Template published'),
        error: () => this.notifyError('Could not publish template')
      });
  }

  archiveTemplate(): void {
    if (!this.canArchive) {
      return;
    }

    this.saving = true;
    this.pdfDocumentBuilderService
      .archiveTemplate(this.selectedTemplate.id)
      .pipe(finalize(() => (this.saving = false)))
      .subscribe({
        next: template => this.openReturnedTemplate(template || { ...this.selectedTemplate, status: 'ARCHIVED' }, 'Template archived'),
        error: () => this.notifyError('Could not archive template')
      });
  }

  createNewVersion(): void {
    if (!this.canCreateNewVersion) {
      return;
    }

    this.saving = true;
    this.pdfDocumentBuilderService
      .createTemplateVersion(this.selectedTemplate.templateGroupId, this.buildRequest())
      .pipe(finalize(() => (this.saving = false)))
      .subscribe({
        next: template => this.openReturnedTemplate(template, 'Draft version created'),
        error: () => this.notifyError('Could not create a new version')
      });
  }

  rollbackTemplate(template: PdfDocumentTemplate = this.selectedTemplate): void {
    if (!this.canRollbackVersion(template)) {
      return;
    }

    this.saving = true;
    this.pdfDocumentBuilderService
      .rollbackTemplate(template.id)
      .pipe(finalize(() => (this.saving = false)))
      .subscribe({
        next: draft => this.openReturnedTemplate(draft, 'Rollback draft created'),
        error: () => this.notifyError('Could not roll back template')
      });
  }

  deleteTemplateVersion(template: PdfDocumentTemplate = this.selectedTemplate): void {
    if (!template?.id || this.deleting) {
      return;
    }

    this.modalService
      .openConfirmYesNoModal('Delete version', `Delete version ${template.version} of "${template.name}"?`, { size: 'sm' }, 'Delete', 'Cancel')
      .then(confirmed => {
        if (!confirmed) {
          return;
        }

        this.deleting = true;
        this.pdfDocumentBuilderService
          .deleteTemplateVersion(template.id)
          .pipe(finalize(() => (this.deleting = false)))
          .subscribe({
            next: () => {
              this.notificationService.notify('', 'Template version deleted', 'success');
              this.loadTemplates();

              if (this.selectedTemplate?.id === template.id) {
                this.openLatestAfterDelete(template.templateGroupId);
              } else if (this.selectedTemplate?.templateGroupId === template.templateGroupId) {
                this.refreshCurrentHistory();
              }
            },
            error: () => this.notifyError('Could not delete template version')
          });
      });
  }

  deleteTemplateGroup(): void {
    if (!this.selectedTemplate?.templateGroupId || this.deleting) {
      return;
    }

    const groupId = this.selectedTemplate.templateGroupId;
    const templateName = this.selectedTemplate.name;

    this.modalService
      .openConfirmYesNoModal('Delete template', `Delete "${templateName}" and all of its versions?`, { size: 'sm' }, 'Delete', 'Cancel')
      .then(confirmed => {
        if (!confirmed) {
          return;
        }

        this.deleting = true;
        this.pdfDocumentBuilderService
          .deleteTemplateGroup(groupId)
          .pipe(finalize(() => (this.deleting = false)))
          .subscribe({
            next: () => {
              this.notificationService.notify('', 'Template deleted', 'success');
              this.clearWorkspaceSelection();
              this.loadTemplates();
            },
            error: () => this.notifyError('Could not delete template')
          });
      });
  }

  getDocumentTypeName(documentTypeId: string): string {
    return this.documentTypes.find(type => type.key === documentTypeId)?.displayName || documentTypeId || 'Unknown type';
  }

  getTemplateSubtitle(template: PdfDocumentTemplate): string {
    const typeName = this.getDocumentTypeName(template.documentTypeId);
    return `${typeName} · v${template.version}`;
  }

  canRollbackVersion(template: PdfDocumentTemplate): boolean {
    return this.mode === 'edit' && !!template?.id && !template.latestVersion && !this.saving && !this.deleting;
  }

  getVersionCreatedBy(template: PdfDocumentTemplate): string {
    return template.createdBy || 'Unknown user';
  }

  getVersionCreatedAge(template: PdfDocumentTemplate): string {
    if (!template?.createdAt) {
      return 'Creation date unavailable';
    }

    const createdAt = new Date(template.createdAt).getTime();
    if (Number.isNaN(createdAt)) {
      return 'Creation date unavailable';
    }

    const seconds = Math.max(0, Math.floor((Date.now() - createdAt) / 1000));
    if (seconds < 60) {
      return 'Just now';
    }

    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) {
      return `${minutes} ${minutes === 1 ? 'minute' : 'minutes'} ago`;
    }

    const hours = Math.floor(minutes / 60);
    if (hours < 24) {
      return `${hours} ${hours === 1 ? 'hour' : 'hours'} ago`;
    }

    const days = Math.floor(hours / 24);
    if (days < 30) {
      return `${days} ${days === 1 ? 'day' : 'days'} ago`;
    }

    const months = Math.floor(days / 30);
    if (months < 12) {
      return `${months} ${months === 1 ? 'month' : 'months'} ago`;
    }

    const years = Math.floor(months / 12);
    return `${years} ${years === 1 ? 'year' : 'years'} ago`;
  }

  trackByTemplateId(index: number, template: PdfDocumentTemplate): string {
    return template.id;
  }

  trackByDocumentType(index: number, documentType: PdfDocumentType): string {
    return documentType.key;
  }

  trackByChatMessageId(index: number, message: PdfDocumentBuilderChatMessage): number {
    return message.id;
  }

  private createEmptyTemplateForm(): PdfDocumentTemplateEditableFields {
    return {
      documentTypeId: '',
      name: '',
      description: '',
      htmlTemplate: ''
    };
  }

  private createEmptyGroupSettingsForm(): PdfDocumentTemplateGroupSettings {
    return {
      displayName: {},
      fileNamePattern: '',
      allowedRoles: [],
      allowedUserGroups: [],
      allowedFlows: []
    };
  }

  get groupSettingsHidden(): boolean {
    return (
      !this.groupSettingsForm.allowedRoles?.length &&
      !this.groupSettingsForm.allowedUserGroups?.length &&
      !this.groupSettingsForm.allowedFlows?.length
    );
  }

  get canSaveGroupSettings(): boolean {
    return this.mode === 'edit' && !!this.selectedTemplate?.templateGroupId && !this.savingGroupSettings;
  }

  onAllowedRolesChanged(values: string[]): void {
    this.groupSettingsForm.allowedRoles = values || [];
  }

  onAllowedUserGroupsChanged(values: string[]): void {
    this.groupSettingsForm.allowedUserGroups = values || [];
  }

  onAllowedFlowsChanged(values: string[]): void {
    this.groupSettingsForm.allowedFlows = values || [];
  }

  private rebuildOptionLists(): void {
    const allowedRoles = this.groupSettingsForm.allowedRoles || [];
    const allowedUserGroups = this.groupSettingsForm.allowedUserGroups || [];
    const allowedFlows = this.groupSettingsForm.allowedFlows || [];

    this.roleOptions = this.availableRoles.map(role => ({
      id: role,
      name: role,
      selected: allowedRoles.includes(role)
    }));
    this.userGroupOptions = this.availableUserGroups.map(group => ({
      id: group,
      name: group,
      selected: allowedUserGroups.includes(group)
    }));
    this.flowOptions = this.availableFlows.map(flow => ({
      id: flow.id,
      name: flow.name || flow.id,
      selected: allowedFlows.includes(flow.id)
    }));
  }

  saveGroupSettings(): void {
    if (!this.canSaveGroupSettings) {
      return;
    }

    const cleanedDisplayName: { [locale: string]: string } = {};
    for (const [locale, label] of Object.entries(this.groupSettingsForm.displayName || {})) {
      const trimmed = (label || '').trim();
      if (trimmed) {
        cleanedDisplayName[locale] = trimmed;
      }
    }

    const body: PdfDocumentTemplateGroupSettings = {
      displayName: cleanedDisplayName,
      fileNamePattern: (this.groupSettingsForm.fileNamePattern || '').trim(),
      allowedRoles: this.groupSettingsForm.allowedRoles || [],
      allowedUserGroups: this.groupSettingsForm.allowedUserGroups || [],
      allowedFlows: this.groupSettingsForm.allowedFlows || []
    };

    this.savingGroupSettings = true;
    this.pdfDocumentBuilderService
      .updateTemplateGroupSettings(this.selectedTemplate.templateGroupId, body)
      .pipe(finalize(() => (this.savingGroupSettings = false)))
      .subscribe({
        next: template => this.openReturnedTemplate(template, 'Settings saved'),
        error: () => this.notifyError('Could not save settings')
      });
  }

  private buildRequest(): PdfDocumentTemplateEditableFields {
    return {
      documentTypeId: this.templateForm.documentTypeId,
      name: this.templateForm.name,
      description: this.templateForm.description || '',
      htmlTemplate: this.templateForm.htmlTemplate || ''
    };
  }

  private applyTemplate(template: PdfDocumentTemplate, versions: PdfDocumentTemplate[] = []): void {
    this.mode = 'edit';
    this.selectedTemplate = template;
    this.versionHistory = versions || [];
    this.templateForm = {
      documentTypeId: template.documentTypeId,
      name: template.name,
      description: template.description || '',
      htmlTemplate: template.htmlTemplate || ''
    };
    this.groupSettingsForm = {
      displayName: { ...(template.displayName || {}) },
      fileNamePattern: template.fileNamePattern || '',
      allowedRoles: [...(template.allowedRoles || [])],
      allowedUserGroups: [...(template.allowedUserGroups || [])],
      allowedFlows: [...(template.allowedFlows || [])]
    };
    this.rebuildOptionLists();
    this.generatedHtmlPendingSave = false;
  }

  private openReturnedTemplate(template: PdfDocumentTemplate, message: string): void {
    this.notificationService.notify('', message, 'success');
    this.openTemplateWithHistory(template);
    this.loadTemplates();
  }

  private openTemplateWithHistory(template: PdfDocumentTemplate): void {
    this.loadingTemplate = true;
    this.pdfDocumentBuilderService
      .getTemplateVersions(template.templateGroupId)
      .pipe(finalize(() => (this.loadingTemplate = false)))
      .subscribe({
        next: versions => {
          this.applyTemplate(template, versions);
          this.loadConversationHistory(template);
        },
        error: () => {
          this.applyTemplate(template, []);
          this.loadConversationHistory(template);
        }
      });
  }

  private refreshCurrentHistory(): void {
    if (!this.selectedTemplate?.templateGroupId) {
      return;
    }

    this.pdfDocumentBuilderService
      .getTemplateVersions(this.selectedTemplate.templateGroupId)
      .subscribe({
        next: versions => (this.versionHistory = versions || []),
        error: () => (this.versionHistory = [])
      });
  }

  private openLatestAfterDelete(templateGroupId: string): void {
    this.pdfDocumentBuilderService
      .getLatestTemplateVersion(templateGroupId)
      .pipe(catchError(() => of(null)))
      .subscribe(latestTemplate => {
        if (latestTemplate) {
          this.openTemplateWithHistory(latestTemplate);
        } else {
          this.clearWorkspaceSelection();
        }
      });
  }

  private clearWorkspaceSelection(): void {
    this.resetGeneratorWorkspace();
    this.mode = 'idle';
    this.selectedTemplate = null;
    this.versionHistory = [];
    this.templateForm = this.createEmptyTemplateForm();
  }

  private notifyError(message: string): void {
    this.notificationService.notify('', message, 'error');
  }

  private ensureDraftForChat(): Observable<PdfDocumentTemplate> {
    if (this.mode === 'edit' && this.selectedTemplate?.id && this.selectedTemplate?.templateGroupId) {
      return of(this.selectedTemplate);
    }

    if (this.mode !== 'new') {
      return throwError(() => new Error('No editable template selected'));
    }

    this.creatingDraftForChat = true;
    return this.pdfDocumentBuilderService.createTemplate(this.buildRequest()).pipe(
      switchMap(template =>
        this.pdfDocumentBuilderService.getTemplateVersions(template.templateGroupId).pipe(
          catchError(() => of([] as PdfDocumentTemplate[])),
          map(versions => {
            this.applyTemplate(template, versions);
            this.loadTemplates();
            return template;
          })
        )
      ),
      finalize(() => (this.creatingDraftForChat = false))
    );
  }

  private getConversationId(template: PdfDocumentTemplate): string {
    return `${template.templateGroupId}-${template.id}`;
  }

  private shouldSendInitialGeneratorContext(): boolean {
    return this.chatMessages.length === 0;
  }

  private buildGeneratorMessage(message: string, includeTemplateContext: boolean): string {
    if (!includeTemplateContext) {
      return message;
    }

    const documentType = this.selectedDocumentType;
    const htmlTemplate = this.templateForm.htmlTemplate?.trim() || '(empty)';
    const contextLines = [
      'This context is provided by the frontend for the first turn only. It is not visible in the chat UI.',
      `documentTypeId: ${this.templateForm.documentTypeId}`,
      documentType?.displayName ? `documentTypeName: ${documentType.displayName}` : '',
      documentType?.description ? `documentTypeDescription: ${documentType.description}` : '',
      documentType?.variableDocumentation ? `documentTypeVariables: ${documentType.variableDocumentation}` : '',
      'currentHtmlTemplate:',
      htmlTemplate
    ].filter(Boolean);

    return `${message}${HIDDEN_GENERATOR_CONTEXT_START}${contextLines.join('\n')}${HIDDEN_GENERATOR_CONTEXT_END}`;
  }

  private handleGeneratorResponse(response: CatalogueGeneratorResponse): void {
    if (response.type === 'question') {
      this.appendChatMessage('assistant', 'question', response.question, response.reason);
      return;
    }

    this.templateForm.htmlTemplate = response.html || '';
    this.generatedHtmlPendingSave = true;
    this.appendChatMessage('assistant', 'result', response.summary || 'Generated a PDF draft.');

    if (response.pdfUrl) {
      this.loadGeneratedPdf(response.pdfUrl);
    }
  }

  private loadGeneratedPdf(pdfUrl: string): void {
    this.pdfPreviewError = '';
    this.generatedPdfUrl = pdfUrl;
    this.generatedPdfSafeUrl = this.sanitizer.bypassSecurityTrustResourceUrl(pdfUrl);
  }

  private loadConversationHistory(template: PdfDocumentTemplate): void {
    if (!template?.id || !template.templateGroupId) {
      return;
    }

    const conversationId = this.getConversationId(template);
    this.loadingChatHistory = true;
    this.pdfDocumentBuilderService
      .getCatalogueHistory(conversationId)
      .pipe(finalize(() => (this.loadingChatHistory = false)))
      .subscribe({
        next: response => {
          if (this.selectedTemplate?.id !== template.id) {
            return;
          }

          const historyItems = this.getHistoryItems(response).map(item => this.getNormalizedHistoryItem(item));
          this.chatMessages = historyItems
            .map(item => this.createChatMessageFromHistory(item))
            .filter((message): message is PdfDocumentBuilderChatMessage => !!message);

          const latestPdfUrl = [...historyItems].reverse().find(item => !!item.pdfUrl)?.pdfUrl;
          if (latestPdfUrl) {
            this.loadGeneratedPdf(latestPdfUrl);
          }

          this.tryResumeActiveJob(template);
        },
        error: () => {
          if (this.selectedTemplate?.id === template.id) {
            this.chatMessages = [];
          }
          this.tryResumeActiveJob(template);
        }
      });
  }

  private tryResumeActiveJob(template: PdfDocumentTemplate): void {
    if (this.selectedTemplate?.id !== template.id) {
      return;
    }

    const conversationId = this.getConversationId(template);

    // If history shows the assistant has already responded to every user
    // message, the job result is already in chat — clear and skip.
    const userCount = this.chatMessages.filter(m => m.role === 'user').length;
    const assistantCount = this.chatMessages.filter(m => m.role === 'assistant').length;
    if (userCount > 0 && assistantCount >= userCount) {
      this.pdfDocumentBuilderService.clearActiveJob(conversationId);
      return;
    }

    const resume$ = this.pdfDocumentBuilderService.resumeGenerationJob(conversationId);
    if (!resume$) {
      return;
    }

    const active = this.pdfDocumentBuilderService.getActiveJob(conversationId);
    this.sendingMessage = true;
    this.generationStartedAt = active?.submittedAt ?? Date.now();
    this.startGenerationStatusTimer();

    resume$
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.sendingMessage = false;
          this.generationStatusText = '';
          this.clearGenerationStatusTimer();
        })
      )
      .subscribe({
        next: response => {
          if (this.selectedTemplate?.id !== template.id) {
            return;
          }
          this.handleGeneratorResponse(response);
        },
        error: error => {
          if (this.selectedTemplate?.id !== template.id) {
            return;
          }
          const message = this.getGeneratorErrorMessage(error);
          this.appendChatMessage('assistant', 'error', message);
        }
      });
  }

  private getHistoryItems(response: CatalogueGeneratorHistoryResponse): CatalogueGeneratorHistoryMessage[] {
    if (Array.isArray(response)) {
      return response;
    }

    return response?.messages || response?.history || response?.conversation || [];
  }

  private createChatMessageFromHistory(item: CatalogueGeneratorHistoryMessage): PdfDocumentBuilderChatMessage | null {
    item = this.getNormalizedHistoryItem(item);

    const rawText =
      item.summary ||
      item.question ||
      item.content ||
      item.text ||
      item.message ||
      (item as any).output ||
      (item.pdfUrl ? 'Generated a PDF draft.' : '');
    const text = this.getVisibleHistoryText(rawText);

    if (!text) {
      return null;
    }

    return {
      id: ++this.chatMessageId,
      role: this.getHistoryMessageRole(item),
      kind: this.getHistoryMessageKind(item),
      text,
      detail: item.reason
    };
  }

  private getNormalizedHistoryItem(item: CatalogueGeneratorHistoryMessage): CatalogueGeneratorHistoryMessage {
    const parsedPayload =
      this.parseHistoryPayload(item.content) ||
      this.parseHistoryPayload(item.text) ||
      this.parseHistoryPayload(item.message) ||
      this.parseHistoryPayload((item as any).output);

    if (!parsedPayload) {
      return item;
    }

    return {
      ...item,
      ...parsedPayload,
      role: item.role || parsedPayload.role,
      sender: item.sender || parsedPayload.sender,
      author: item.author || parsedPayload.author,
      kind: item.kind || parsedPayload.kind,
      type: parsedPayload.type || item.type,
      content: parsedPayload.content,
      text: parsedPayload.text,
      message: parsedPayload.message
    };
  }

  private parseHistoryPayload(value: unknown): CatalogueGeneratorHistoryMessage | null {
    if (typeof value !== 'string') {
      return null;
    }

    const trimmedValue = value.trim();
    if (!trimmedValue.startsWith('{') && !trimmedValue.startsWith('[')) {
      return null;
    }

    try {
      const parsedValue = JSON.parse(trimmedValue);
      if (!parsedValue || Array.isArray(parsedValue) || typeof parsedValue !== 'object') {
        return null;
      }

      const parsedMessage = parsedValue as CatalogueGeneratorHistoryMessage;
      if (
        parsedMessage.type ||
        parsedMessage.summary ||
        parsedMessage.question ||
        parsedMessage.reason ||
        parsedMessage.pdfUrl ||
        parsedMessage.html
      ) {
        return parsedMessage;
      }

      return null;
    } catch {
      return null;
    }
  }

  private getVisibleHistoryText(text: string): string {
    const hiddenContextIndex = text.indexOf(HIDDEN_GENERATOR_CONTEXT_START);
    if (hiddenContextIndex === -1) {
      return text;
    }

    return text.substring(0, hiddenContextIndex).trim();
  }

  private getHistoryMessageRole(item: CatalogueGeneratorHistoryMessage): PdfDocumentBuilderChatRole {
    const roleValue = `${item.role || item.sender || item.author || (item as any).messageType || item.type || ''}`.toLowerCase();
    return roleValue.includes('user') || roleValue.includes('human') ? 'user' : 'assistant';
  }

  private getHistoryMessageKind(item: CatalogueGeneratorHistoryMessage): PdfDocumentBuilderChatKind {
    const kindValue = `${item.kind || item.type || ''}`.toLowerCase();

    if (kindValue.includes('question')) {
      return 'question';
    }

    if (kindValue.includes('result')) {
      return 'result';
    }

    if (kindValue.includes('error')) {
      return 'error';
    }

    return 'text';
  }

  private appendChatMessage(
    role: PdfDocumentBuilderChatRole,
    kind: PdfDocumentBuilderChatKind,
    text: string,
    detail?: string
  ): void {
    this.chatMessages = [
      ...this.chatMessages,
      {
        id: ++this.chatMessageId,
        role,
        kind,
        text,
        detail
      }
    ];
  }

  private resetGeneratorWorkspace(): void {
    this.chatDraft = '';
    this.chatMessages = [];
    this.chatMessageId = 0;
    this.pendingAttachments = [];
    this.loadingChatHistory = false;
    this.generatedHtmlPendingSave = false;
    this.pdfPreviewError = '';
    this.sourceExpanded = false;
    this.generatedPdfUrl = '';
    this.generatedPdfSafeUrl = null;
    this.groupSettingsForm = this.createEmptyGroupSettingsForm();
    this.groupSettingsExpanded = false;
    this.rebuildOptionLists();
    this.clearGenerationStatusTimer();
  }

  private getGeneratorErrorMessage(error: any): string {
    return error?.error?.error?.message || error?.error?.message || error?.message || 'Could not generate catalogue';
  }
}
